/**
 * =============================================================================
 * FECHAS, HORAS Y NUMEROS SEGUN EL IDIOMA ACTIVO
 * =============================================================================
 * Traducir las palabras no traduce los numeros. El portal tenia hoy tres
 * averias distintas repartidas por 17 sitios:
 *
 * 1. `toLocaleDateString("es-ES")` — el idioma ESPANOL CLAVADO en el codigo.
 *    Aunque el portal este en frances, esa fecha sigue saliendo en espanol.
 *
 * 2. `toLocaleDateString()` sin argumentos — sigue al NAVEGADOR, no al idioma
 *    elegido en la interfaz. Un terapeuta con Windows en ingles ve el portal en
 *    castellano y las fechas en formato americano (mes/dia), que para el 12 de
 *    marzo se lee "3/12" y para el 3 de diciembre tambien. Es la peor de las
 *    tres porque no parece un fallo: parece una fecha.
 *
 * 3. `dayjs().format("DD/MM/YYYY HH:mm")` — formato fijo que no depende de nada.
 *
 * Aqui la region sale SIEMPRE del idioma de la interfaz. Es una decision:
 * quien elige frances quiere leer el portal entero en frances, incluidas las
 * fechas, aunque su sistema operativo este en otra cosa.
 *
 * Las funciones son puras y reciben el idioma; el hook `useFormatos()` lo
 * enchufa al idioma activo para los componentes.
 * =============================================================================
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_LANGUAGE } from "./config";

/**
 * La region importa: "fr" a secas deja que Intl elija, y no siempre elige
 * Francia (fr-CA escribe las fechas al reves).
 */
const REGION_POR_IDIOMA = {
  es: "es-ES",
  fr: "fr-FR",
};

/** Lo que se pinta cuando no hay dato. Antes salia "Invalid Date". */
export const SIN_DATO = "—";

export function regionDe(lng) {
  const base = String(lng || DEFAULT_LANGUAGE)
    .slice(0, 2)
    .toLowerCase();
  return REGION_POR_IDIOMA[base] || REGION_POR_IDIOMA[DEFAULT_LANGUAGE];
}

/**
 * Convierte a Date lo que llegue, o null si no es una fecha.
 *
 * En este portal las fechas llegan de la API como cadena ISO, de Mongo como
 * Date, y de un formulario a medio rellenar como undefined o "". `new Date()`
 * se traga las tres y devuelve "Invalid Date" para las malas, que es lo que un
 * terapeuta acaba viendo en la ficha de un paciente sin fecha de nacimiento.
 */
function aFecha(valor) {
  if (valor === null || valor === undefined || valor === "") return null;
  const d = valor instanceof Date ? valor : new Date(valor);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** 09/08/2026 en es-ES, 09/08/2026 en fr-FR. */
export function formatearFecha(valor, lng) {
  const d = aFecha(valor);
  if (!d) return SIN_DATO;
  return new Intl.DateTimeFormat(regionDe(lng), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
}

/**
 * Fecha con hora, en 24h en los dos idiomas.
 *
 * `hourCycle: "h23"` no es decoracion: sin el, Intl decide por region y algunas
 * variantes de espanol devuelven "1:05 p. m." en una tabla clinica donde el
 * resto de filas van en 24h.
 */
export function formatearFechaHora(valor, lng) {
  const d = aFecha(valor);
  if (!d) return SIN_DATO;
  return new Intl.DateTimeFormat(regionDe(lng), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(d);
}

/** Fecha larga: "9 de agosto de 2026" / "9 août 2026". */
export function formatearFechaLarga(valor, lng) {
  const d = aFecha(valor);
  if (!d) return SIN_DATO;
  return new Intl.DateTimeFormat(regionDe(lng), {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(d);
}

/**
 * Numero con los separadores del idioma.
 *
 * El frances escribe 1 234,5 donde el castellano escribe 1.234,5: separador de
 * millares distinto y coma decimal en los dos. Pegar el numero crudo con
 * `${valor}` da siempre el formato ingles (1234.5).
 */
export function formatearNumero(valor, lng, opciones = {}) {
  if (valor === null || valor === undefined || valor === "") return SIN_DATO;
  const n = Number(valor);
  if (!Number.isFinite(n)) return SIN_DATO;
  return new Intl.NumberFormat(regionDe(lng), opciones).format(n);
}

/** Porcentaje ya calculado (85 -> "85 %"). */
export function formatearPorcentaje(valor, lng, decimales = 0) {
  if (valor === null || valor === undefined || valor === "") return SIN_DATO;
  const n = Number(valor);
  if (!Number.isFinite(n)) return SIN_DATO;
  return new Intl.NumberFormat(regionDe(lng), {
    style: "percent",
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(n / 100);
}

/**
 * Duracion en "3 min 05 s".
 *
 * `min` y `s` son simbolos del SI: valen igual en castellano y en frances, asi
 * que no hay que traducirlos. Lo que si cambia respecto a lo que habia
 * (`3m 05s`) es que ahora llevan espacio, que es como se escribe una unidad.
 */
export function formatearDuracion(segundos, lng) {
  const n = Number(segundos);
  if (!Number.isFinite(n) || n < 0) return SIN_DATO;
  const total = Math.floor(n);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${formatearNumero(m, lng)} min ${String(s).padStart(2, "0")} s`;
}

/**
 * "hace 3 dias", "il y a 3 jours", "3 days ago".
 *
 * POR QUE RELATIVA Y NO LA FECHA. En una lista de tests, la pregunta del
 * terapeuta no es "que dia fue" sino "cuanto hace". "12/07/2026" obliga a
 * restar mentalmente contra el dia de hoy, una vez por fila; "hace un mes" se
 * responde sola. La fecha exacta sigue estando a un paso, en las graficas.
 *
 * `Intl.RelativeTimeFormat` se encarga del idioma y de los plurales, que en
 * frances no son los del castellano. `numeric: "auto"` es lo que convierte
 * "hace 1 dia" en "ayer" y "hace 0 dias" en "hoy", que es como se habla.
 *
 * Se elige la unidad mas grande que no de un numero ridiculo: 45 dias se
 * cuentan en meses, no en dias, porque "hace 45 dias" no se visualiza.
 */
export function formatearFechaRelativa(valor, lng, ahora = Date.now()) {
  const d = aFecha(valor);
  if (!d) return SIN_DATO;

  const segundos = Math.round((d.getTime() - ahora) / 1000);
  const abs = Math.abs(segundos);
  const rtf = new Intl.RelativeTimeFormat(regionDe(lng), { numeric: "auto" });

  // Ordenadas de menor a mayor. La primera cuyo umbral no se supera manda.
  const ESCALA = [
    { limite: 60, division: 1, unidad: "second" },
    { limite: 3600, division: 60, unidad: "minute" },
    { limite: 86400, division: 3600, unidad: "hour" },
    // Hasta 4 semanas se cuenta en dias: es la unidad con la que un terapeuta
    // razona sobre la frecuencia de las mediciones.
    { limite: 2592000, division: 86400, unidad: "day" },
    { limite: 31536000, division: 2592000, unidad: "month" },
    { limite: Infinity, division: 31536000, unidad: "year" },
  ];

  const tramo = ESCALA.find((e) => abs < e.limite);
  return rtf.format(Math.round(segundos / tramo.division), tramo.unidad);
}

/**
 * Bandeja de formateadores ya atados al idioma activo.
 *
 * Se memoiza por idioma porque construir un Intl.DateTimeFormat no es gratis y
 * estas funciones se llaman una vez por fila de tabla.
 */
export function useFormatos() {
  const { i18n } = useTranslation();
  const lng = i18n.language;

  return useMemo(
    () => ({
      lng,
      fecha: (v) => formatearFecha(v, lng),
      fechaHora: (v) => formatearFechaHora(v, lng),
      fechaLarga: (v) => formatearFechaLarga(v, lng),
      fechaRelativa: (v) => formatearFechaRelativa(v, lng),
      numero: (v, o) => formatearNumero(v, lng, o),
      porcentaje: (v, d) => formatearPorcentaje(v, lng, d),
      duracion: (v) => formatearDuracion(v, lng),
    }),
    [lng],
  );
}
